import {useState, useCallback, useMemo} from 'react';
import {styled} from 'styled-components';

import {entries} from '@shared/lib/utils/map_utils';
import {Door, DoorMessageCategory} from '@shared/models/api_models';

import {Button, UnthemedButton} from '@shared_web/components/core/button';
import {Checkbox} from '@shared_web/components/core/checkbox';
import {Input} from '@shared_web/components/core/input';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Custom} from '@shared_web/lib/react';
import {useGlobalKeyPress} from '@shared_web/lib/use_global_key_press';

import {Loader} from '@mavismart_frontend/components/loader';
import {findTranslationKey, Translate, translate} from '@mavismart_frontend/components/translate';
import {icon_chevron_right} from '@mavismart_frontend/icons/icon_chevron_right';
import {icon_warning_green} from '@mavismart_frontend/icons/icon_warning_green';
import {icon_warning_orange} from '@mavismart_frontend/icons/icon_warning_orange';
import {icon_warning_red} from '@mavismart_frontend/icons/icon_warning_red';
import {
  BORDER_COLOR,
  BUTTON_THEME,
  INPUT_THEME,
  TEXT_MAIN_COLOR,
} from '@mavismart_frontend/lib/constants';
import {applySearchQueryOnDoor, DoorSearchField} from '@mavismart_frontend/lib/door_search';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {useAllDoors} from '@mavismart_frontend/stores/all_doors_store';

export type DoorSearchResult = {
  door: Door;
  matches: {field: DoorSearchField; index: number; score: number}[];
  maxScore: number;
  sumScore: number;
};

export type AdvancedSearchResultsData = {
  results: DoorSearchResult[];
  searchQuery: string;
};

type AdvancedSearchFormProps = {
  title?: React.ReactNode;
  expandedOnMount?: boolean;
  onResults?: (res: AdvancedSearchResultsData) => void;
};

export const AdvancedSearchForm: Custom<AdvancedSearchFormProps, 'div', 'title'> = props => {
  const {title, expandedOnMount, onResults, ...rest} = props;

  const account = useAccount();
  const allDoors = useAllDoors();
  const STATUS_MODEL = ['green', 'yellow', 'red'];
  const isLoading = allDoors === undefined;

  const doorModels = useMemo(() => {
    if (!allDoors?.doors) {
      return [];
    }
    const models = new Set<string>();
    for (const [, door] of allDoors.doors) {
      if (door.name1 !== undefined) {
        const doorModel = door.name1.split(' ')[0];
        if (doorModel !== undefined && doorModel.toLowerCase() !== 'mavione') {
          models.add(doorModel);
        }
      }
    }
    return Array.from(models).sort((a, b) => a.localeCompare(b));
  }, [allDoors]);

  const doorCategories = useMemo(() => {
    if (!allDoors?.doors) {
      return new Map<string, number>();
    }
    const categoriesCount = new Map<DoorMessageCategory, number>();
    for (const [, door] of allDoors.doors) {
      for (const [category, level] of entries(door.categoryLevels ?? {})) {
        if (level === 'red' || level === 'yellow') {
          categoriesCount.set(category, (categoriesCount.get(category) ?? 0) + 1);
        }
      }
    }
    return categoriesCount;
  }, [allDoors]);

  const doorAlertStats = useMemo(() => {
    if (!allDoors?.doors) {
      return {totalDoors: 0, doorsWithAlerts: 0};
    }
    const totalDoors = allDoors.doors.size;
    let doorsWithAlerts = 0;
    for (const [, door] of allDoors.doors) {
      const hasAlert = Object.values(door.categoryLevels ?? {}).some(
        level => level === 'red' || level === 'yellow'
      );
      if (hasAlert) {
        doorsWithAlerts++;
      }
    }
    return {totalDoors, doorsWithAlerts};
  }, [allDoors]);

  const doorStatusCounts = useMemo(() => {
    if (!allDoors?.doors) {
      return {green: 0, yellow: 0, red: 0};
    }
    let green = 0;
    let yellow = 0;
    let red = 0;
    for (const [, door] of allDoors.doors) {
      if (door.status === 'green' || door.status === undefined) {
        green++;
      } else if (door.status === 'yellow') {
        yellow++;
      } else if (door.status === 'red') {
        red++;
      }
    }
    return {green, yellow, red};
  }, [allDoors]);

  const [isDoorsModelOpen, setIsDoorsModelOpen] = useState<boolean>(expandedOnMount ?? false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState<boolean>(expandedOnMount ?? false);
  const [isStatusOpen, setIsStatusOpen] = useState<boolean>(expandedOnMount ?? false);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<DoorMessageCategory>>(new Set());
  const [commissioningDate, setCommissioningDate] = useState<string>('');
  const [manufactureYear, setManufactureYear] = useState<string>('');
  const [doorSearchText, setDoorSearchText] = useState<string>('');
  const [status, setStatus] = useState<'green' | 'yellow' | 'red' | undefined>();

  const handleDoorModelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedModels(prev => {
      if (prev.includes(value)) {
        return prev.filter(item => item !== value);
      }
      return [...prev, value];
    });
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as DoorMessageCategory;
    setSelectedCategories(prev => {
      if (prev.has(value)) {
        const newSet = new Set(prev);
        newSet.delete(value);
        return newSet;
      }
      return new Set(prev).add(value);
    });
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'green' | 'yellow' | 'red';
    setStatus(prev => (prev === value ? undefined : value));
  }, []);

  const dateHandleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCommissioningDate(e.target.value);
  }, []);

  const manufactureYearHandleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManufactureYear(e.target.value);
  }, []);

  const doorSearchTextHandleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDoorSearchText(e.target.value);
  }, []);

  const toggleDoorsModel = useCallback(() => {
    setIsDoorsModelOpen(!isDoorsModelOpen);
  }, [isDoorsModelOpen]);

  const toggleCategories = useCallback(() => {
    setIsCategoriesOpen(!isCategoriesOpen);
  }, [isCategoriesOpen]);

  const toggleStatus = useCallback(() => {
    setIsStatusOpen(!isStatusOpen);
  }, [isStatusOpen]);

  const handleSearch = useCallback(() => {
    if (!allDoors) {
      // Doors are still loading, ignore the search action for now
      return;
    }

    const doorWithMatches: DoorSearchResult[] = [];
    for (const [, door] of allDoors.doors) {
      let isModel = false;
      let isYear = false;
      let isDate = false;
      let isStatus = false;
      let isCategory = false;

      if (door.name1) {
        for (const model of selectedModels) {
          if (door.name1.toUpperCase().startsWith(model.toUpperCase())) {
            isModel = true;
            break;
          }
        }
      }

      if (door.categoryLevels !== undefined && selectedCategories.size > 0) {
        for (const category of selectedCategories) {
          const level = door.categoryLevels[category];
          if (level !== undefined && level !== 'green') {
            isCategory = true;
            break;
          }
        }
      }

      if (door.setupDate) {
        const year = door.setupDate.split('/')[2];
        if (year === manufactureYear) {
          isYear = true;
        }

        const parts = door.setupDate.split('/'); // ["03","07","2024"]
        if (parts.length === 3) {
          const day = Number(parts[0]);
          const month = Number(parts[1]);
          const year = Number(parts[2]);

          const setupDateObj = new Date(year, month - 1, day);
          const referenceDate = new Date(commissioningDate);

          if (setupDateObj > referenceDate) {
            isDate = true;
          }
        }
      }
      if (door.status === status || (door.status === undefined && status === 'green')) {
        isStatus = true;
      }

      const matchesModel =
        selectedModels.length > 0
          ? isModel // au moins un modèle choisi → doit matcher
          : true; // aucun modèle choisi → on ignore le critère
      const matchesCategory = selectedCategories.size > 0 ? isCategory : true;
      const matchesYear = manufactureYear !== '' ? isYear : true;
      const matchesCommissioning = commissioningDate !== '' ? isDate : true;
      const matchesStatus = status !== undefined ? isStatus : true;

      if (
        !(matchesModel && matchesCategory && matchesYear && matchesCommissioning && matchesStatus)
      ) {
        continue;
      }

      // Door passed basic filtering, now apply search query
      const {matches, maxScore, sumScore} = applySearchQueryOnDoor({
        searchQuery: doorSearchText,
        door,
      });
      const globalScore = maxScore * 1e6 + sumScore;
      // Include doors that match filters even if search query is empty
      if (globalScore > 0 || doorSearchText.trim() === '') {
        doorWithMatches.push({door, matches, maxScore, sumScore});
      }
    }

    const sortedResults = doorWithMatches.sort((a, b) => {
      const scoreA = a.maxScore * 1e6 + a.sumScore;
      const scoreB = b.maxScore * 1e6 + b.sumScore;
      return scoreB - scoreA;
    });
    onResults?.({results: sortedResults, searchQuery: doorSearchText});
  }, [
    allDoors,
    selectedModels,
    selectedCategories,
    commissioningDate,
    status,
    manufactureYear,
    onResults,
    doorSearchText,
  ]);

  // Perform search when pressing enter key
  useGlobalKeyPress(['Enter'], handleSearch);

  return (
    <SearchWrapper {...rest}>
      <SearchContentFadeTop />
      <SearchContent>
        {title}
        <Input
          type="text"
          width="100%"
          label="Search in door details"
          value={doorSearchText}
          onChange={doorSearchTextHandleChange}
          autoComplete="off"
          themeOverrides={{input: INPUT_THEME}}
        />
        <InputDateStyled
          type="date"
          width="100%"
          label={translate('commissioning_after', getUserLanguage(account))}
          value={commissioningDate}
          onChange={dateHandleChange}
          autoComplete="off"
          themeOverrides={{input: INPUT_THEME}}
        />
        <Input
          type="number"
          width="100%"
          label={translate('manufacture_year', getUserLanguage(account))}
          value={manufactureYear}
          onChange={manufactureYearHandleChange}
          autoComplete="off"
          themeOverrides={{input: INPUT_THEME}}
        />

        <WrapperBox>
          <UnthemedButtonStyled onClick={toggleDoorsModel} $isOpen={isDoorsModelOpen}>
            {translate('doors_model', getUserLanguage(account))}
            <SvgIconChevron
              icon={icon_chevron_right}
              color="transparent"
              size={16}
              $isOpen={isDoorsModelOpen}
            />
          </UnthemedButtonStyled>
          {isDoorsModelOpen && (
            <WrapperCheckbox>
              {isLoading ? (
                <LoaderWrapper>
                  <Loader />
                </LoaderWrapper>
              ) : (
                doorModels.map(model => (
                  <CheckboxRow key={model}>
                    <CheckboxStyled
                      value={model}
                      checked={selectedModels.includes(model)}
                      onChange={handleDoorModelChange}
                      width="100%"
                    >
                      <CheckboxLabelText>{model}</CheckboxLabelText>
                    </CheckboxStyled>
                  </CheckboxRow>
                ))
              )}
            </WrapperCheckbox>
          )}
        </WrapperBox>
        <WrapperBox>
          <UnthemedButtonStyled onClick={toggleStatus} $isOpen={isStatusOpen}>
            {translate('status', getUserLanguage(account))}
            <SvgIconChevron
              icon={icon_chevron_right}
              color="transparent"
              size={16}
              $isOpen={isStatusOpen}
            />
          </UnthemedButtonStyled>
          {isStatusOpen && (
            <WrapperCheckbox>
              {STATUS_MODEL.map(s => (
                <CheckboxStyled
                  key={s}
                  value={s}
                  checked={s === status}
                  onChange={handleStatusChange}
                >
                  {s === 'green' ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <Translate value="alert_resolved" /> ({doorStatusCounts.green})
                      <SvgIcon icon={icon_warning_green} color="transparent" />
                    </div>
                  ) : s === 'yellow' ? (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <Translate value="alert_to_watch" /> ({doorStatusCounts.yellow})
                      <SvgIcon icon={icon_warning_orange} color="transparent" />
                    </div>
                  ) : (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <Translate value="alert_blocking" /> ({doorStatusCounts.red})
                      <SvgIcon icon={icon_warning_red} color="transparent" />
                    </div>
                  )}
                </CheckboxStyled>
              ))}
            </WrapperCheckbox>
          )}
        </WrapperBox>
        {doorCategories.size > 0 && (
          <WrapperBox>
            <UnthemedButtonStyled onClick={toggleCategories} $isOpen={isCategoriesOpen}>
              <Translate value="alerts" /> ({doorAlertStats.doorsWithAlerts}/
              {doorAlertStats.totalDoors})
              <SvgIconChevron
                icon={icon_chevron_right}
                color="transparent"
                size={16}
                $isOpen={isCategoriesOpen}
              />
            </UnthemedButtonStyled>
            {isCategoriesOpen && (
              <WrapperCheckbox>
                {isLoading ? (
                  <LoaderWrapper>
                    <Loader />
                  </LoaderWrapper>
                ) : (
                  Array.from(doorCategories.entries())
                    .sort()
                    .map(([category, count]) => {
                      const translateKey = findTranslationKey(category);
                      const translatedCategory = translateKey
                        ? translate(translateKey, getUserLanguage(account))
                        : category;
                      const categoryText = `${translatedCategory} (${count})`;
                      return (
                        <CheckboxRow key={category}>
                          <CheckboxStyled
                            value={category}
                            checked={selectedCategories.has(category as DoorMessageCategory)}
                            onChange={handleCategoryChange}
                            width="100%"
                          >
                            <CheckboxLabelText>{categoryText}</CheckboxLabelText>
                          </CheckboxStyled>
                        </CheckboxRow>
                      );
                    })
                )}
              </WrapperCheckbox>
            )}
          </WrapperBox>
        )}
      </SearchContent>
      <SearchContentFadeBottom />
      <SearchFooter>
        <Button themeOverrides={BUTTON_THEME} expand onClick={handleSearch} disabled={isLoading}>
          {translate(
            isLoading ? 'search_form_button_loading_placeholder' : 'search_form_button',
            getUserLanguage(account)
          )}
        </Button>
      </SearchFooter>
    </SearchWrapper>
  );
};

AdvancedSearchForm.displayName = 'AdvancedSearchForm';

const GAP = '20px';

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding-bottom: ${GAP}; // Handling padding in children so that input focus outline is shown
`;

const SearchContent = styled.div`
  height: 0;
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${GAP};
  padding: ${GAP};
  box-sizing: border-box;
  overflow-y: auto;
`;

const SearchContentFadeTop = styled.div`
  z-index: 10;
  flex-shrink: 0;
  height: ${GAP};
  margin-bottom: -${GAP};
  width: 100%;
  background: linear-gradient(to top, rgba(255, 255, 255, 0) 0%, #ffffff 90%);
`;

const SearchContentFadeBottom = styled.div`
  z-index: 10;
  flex-shrink: 0;
  height: ${GAP};
  margin-top: -${GAP};
  width: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ffffff 90%);
`;

const SearchFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 ${GAP};
  width: 100%;
`;

const WrapperBox = styled.div`
  border: 1px solid ${BORDER_COLOR};
  border-radius: 8px;
  &:hover {
    border-color: ${TEXT_MAIN_COLOR};
  }
`;

const UnthemedButtonStyled = styled(UnthemedButton)<{$isOpen: boolean}>`
  display: flex;
  justify-content: space-between;  
  width: 100%;
  height: 50px;
  padding: 10px 10px 10px 12px;
  font-size: 16px;
  background-color: transparent;
  color: ${TEXT_MAIN_COLOR};
  box-sizing: border-box;
  ${({$isOpen}) =>
    $isOpen &&
    `
    border-bottom: 1px solid #EEF1F4;
  `}

  
`;

const WrapperCheckbox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: 10px;
  max-height: 256px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0 12px;
`;

const SvgIconChevron = styled(SvgIcon)<{$isOpen: boolean}>`
  transition: transform 0.2s ease-in-out;
  ${({$isOpen}) =>
    $isOpen &&
    `
    transform: rotate(90deg);
  `}
`;

const CheckboxStyled = styled(Checkbox)`
  &:before {
    border-color: ${TEXT_MAIN_COLOR};
  }
  &:checked:before {
    background-color: ${TEXT_MAIN_COLOR};
    border-color: ${TEXT_MAIN_COLOR};
  }
  &:checked:after {
    content: '✓'; 
    position: absolute;
    color: white;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const CheckboxLabelText = styled.span`
  display: inline-block;
  width: 0;
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InputDateStyled = styled(Input)`
  text-align: center;
  line-height: 48px;
  padding: 0px 7px 0 9px;
  min-width: 135px;
`;
